import React, { useState, useCallback } from 'react';
import { useLocaleNavigate } from '../../../../../hooks/useLocaleNavigate';
import { Box, Button } from '@lucid/lego';
import { useCartContext, useDeleteCart } from '@lucid/cart';
import { useLabels } from '../../../../../hooks/useLabels';
import { convertToContentfulLocale } from '../../../../../utils/tools';
import Header from './Header/Header';
import SummaryPanel from './SummaryPanel/SummaryPanel';
import CartToolbar from './CartToolbar/CartToolbar';
import VehicleList from './VehicleList/VehicleList';

export type ContentProps = {
  theme: 'dark' | 'light';
  locale: string;
  customerId: string;
};

/**
 * Checkout page content — rendered inside CartProvider to access cart context.
 * @component
 * @param {ContentProps} props
 * @returns {JSX.Element}
 * @example
 * <Content theme="light" locale="en-US" customerId="abc123" />
 */
const Content: React.FC<ContentProps> = ({ theme, locale, customerId }) => {
  const navigate = useLocaleNavigate();
  const { activeCart, isLoading } = useCartContext();
  const { data: labels } = useLabels({
    id: '',
    locale: convertToContentfulLocale(locale),
  });

  const pricing = activeCart?.pricingSummary;
  const currencyCode = activeCart?.currencyCode ?? pricing?.currencyCode;
  const totalVehicles = activeCart?.itemCount ?? 0;
  const items = activeCart?.items ?? [];

  const cartId = activeCart?.cartId ?? '';
  const { mutate: deleteCart, isPending: isDeletingCart } = useDeleteCart({ cartId, customerId });

  const invertedTheme = theme === 'dark' ? 'light' : 'dark';
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const toggleItem = useCallback((id: string) => {
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  return (
    <Box theme={theme} data-testid="order-builder-checkout-page">
      <Header theme={theme} labels={labels} onSubmit={() => {}} />
      <Button
        type="tertiary"
        color="neutral"
        theme={theme}
        dataTestId="checkout-back-button"
        gaComponent="order-builder"
        label={labels?.back ?? 'Back'}
        iconLeft={{ category: 'arrows', name: 'arrow-left' }}
        handleClick={() => navigate(-1)}
      />
      <SummaryPanel
        labels={labels}
        pricing={pricing}
        currencyCode={currencyCode}
        totalVehicles={totalVehicles}
      />
      <CartToolbar
        theme={theme}
        invertedTheme={invertedTheme}
        labels={labels}
        totalVehicles={totalVehicles}
        pricing={pricing}
        currencyCode={currencyCode}
        onDeleteCart={() =>
          deleteCart(undefined, { onSuccess: () => navigate('/order-hub/order-builder') })
        }
        isDeletingCart={isDeletingCart}
        onConfigureNewVehicle={() => navigate('/order-hub/order-builder#configurator')}
        onAddAvailableVehicle={() => navigate('/order-hub/order-builder#available-vehicles')}
      />
      <VehicleList
        theme={theme}
        labels={labels}
        isLoading={isLoading}
        items={items}
        currencyCode={currencyCode}
        expandedItems={expandedItems}
        onToggle={toggleItem}
        customerId={customerId}
      />
    </Box>
  );
};

export default Content;
